import { useWidget } from '@telescope/cassini-hooks';
import { Box, Button, FormControl, VStack } from '@telescope/cassini-ui';
import { GenericObject } from '@telescope/cassini-utilities';
import React, { useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useAuth } from '../lib';
import { AuthMethod } from '../utils';
import { InputName, renderInput } from 'components';
import { ErrorWarning } from './ErrorWarning';
import { isEmpty } from 'lodash';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from 'utils';
import { Open } from 'types';
interface RegisterFormStep2Props {
  onFormSubmit(formData: GenericObject): void;
  defaultValues?: GenericObject;
}
export const RegisterFormStep2 = ({ onFormSubmit, defaultValues = {} }: RegisterFormStep2Props) => {
  const { isRegistering } = useAuth();
  const { data: authData } = useWidget({ select: (data: Open) =>  data.widget.global_views.auth });
  const { content: { registrationModal: content }, settings: { isAgeGateEnabled, minAge } } = authData!;
  const formMethods = useForm<{ [x: string]: any }>({
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues,
  });
  const { handleSubmit, getValues, setError, formState: { errors } } = formMethods;
  const hasError = !isEmpty(errors);

  const { isMethodEnabled } = useAuth();

  const navigate = useNavigate();

  const isAgeAllowed = useCallback(() => {
    if (!isAgeGateEnabled) return true;

    const dob = new Date(getValues(content.dobInput.fieldType));
    const now = new Date();
    let yearsDiff = now.getFullYear() - dob.getFullYear();

    return yearsDiff >= parseInt(minAge, 10);
  }, [isAgeGateEnabled]);

  const handleCompleteRegistration = () => {
    handleSubmit((data) => {

      if (!isAgeAllowed()) {
        const errorMessage = content.dobInput.notAllowedErrorMessage.replace('{{MIN_AGE}}', minAge); 
        return setError(InputName.DOB, { type: 'notAllowed', message: errorMessage });
      }

      onFormSubmit(data);
    })();
  };

  return (
    <FormProvider {...formMethods}>
      <FormControl isInvalid={!!hasError}>
        <ErrorWarning>
          {content.errorMessage.generic}
        </ErrorWarning>
      </FormControl>

      <form onSubmit={(e) => e.preventDefault()}>
        <VStack spacing={5}>
          {/* Email */}
          {isMethodEnabled(AuthMethod.EMAIL) && (
            <Box w="100%">
              {renderInput({
                ...content.emailInput,
                isDisabled: defaultValues.user_email,
              })}
            </Box>
          )}

          {isAgeGateEnabled && (
            <Box w="100%">
              {renderInput({ ...content.dobInput })}
            </Box>
          )}

          {content.inputs?.map((field: any, index: number) => {
            return <React.Fragment key={`inputs-${index}`}>{renderInput({ ...field })}</React.Fragment>;
          })}

          {content.optins?.[0]?.fieldType &&
            <Box my={4} w="100%" textAlign="left">
              {content.optins.map((field: any, index: number) => {
                return <React.Fragment key={`optin-${index}`}>{renderInput({ ...field })}</React.Fragment>;
              })}
            </Box>}

          <Button isLoading={isRegistering} width="full" type="button" mb={4} onClick={handleCompleteRegistration}>
            {content.continueButton}
          </Button>

          <Button
            size="md"
            type="button"
            variant="link"
            onClick={() => navigate(ROUTES.LOGIN, { replace: true, state: { backgroundLocation: ROUTES.HOME } })}>
            {content.loginLink}
          </Button>

        </VStack>
      </form>
    </FormProvider>
  );
};
